import { logError } from '@zg-rentals/log-error';
import type { Amount, CurrencyType } from 'app/types/costAndFees.type';

/**
 * Parses an Amount object (numeric or displayText) into a numeric {min, max} structure.
 * Returns {min: 0, max: 0} if parsing fails or input is invalid.
 */
export function parseNumericAmount(amount: Amount | null | undefined): { min: number; max: number | null } {
  if (!amount) {
    return { min: 0, max: 0 };
  }

  if ('displayText' in amount) {
    const match = amount.displayText.match(/\$?([\d,]+)(\s*-\s*\$?([\d,]+))?/);
    if (match) {
      try {
        const minText = match[1].replace(/,/g, '');
        const maxText = match[3]?.replace(/,/g, '');

        const min = parseFloat(minText);
        const max = maxText ? parseFloat(maxText) : min;

        if (!isNaN(min) && !isNaN(max)) {
          return { min, max: Math.max(min, max) };
        }
      } catch (err) {
        logError({
          error: err,
          errorType: 'CostsAndFeesError',
          context: 'Error parsing amount displayText',
        });
      }
    }

    return { min: 0, max: 0 };
  } else if ('min' in amount) {
    const min = amount.min ?? 0;
    const max = amount.max;
    const validatedMin = isNaN(min) ? 0 : min;
    const validatedMax = typeof max === 'number' ? (isNaN(max) ? validatedMin : Math.max(validatedMin, max)) : null;

    return { min: validatedMin, max: validatedMax };
  }

  return { min: 0, max: 0 };
}

/**
 * Adds two Amount objects numerically.
 * If either amount has displayText, the result becomes displayText "Contact" (or similar).
 * If either amount has max: null, the result max becomes null.
 * Treats unparseable displayText amounts effectively as 0 for numeric calculation if the other amount is numeric.
 */
export function addAmounts(amount1: Amount, amount2: Amount | null | undefined): Amount {
  if (!amount2) return amount1;

  if ('displayText' in amount1 || 'displayText' in amount2) {
    return { displayText: 'Contact' };
  }

  const value1 = parseNumericAmount(amount1);
  const value2 = parseNumericAmount(amount2);

  const resultMin = value1.min + value2.min;
  let resultMax: number | null;

  if (value1.max === null || value2.max === null) {
    resultMax = null;
  } else {
    resultMax = value1.max + value2.max;
  }

  return {
    min: resultMin,
    max: resultMax,
  };
}

/**
 * Multiplies a numeric Amount object by a quantity.
 * If the amount has displayText, returns it unchanged.
 * If the amount has max: null, the result max remains null.
 * Handles non-numeric or negative quantities.
 */
export function multiplyAmount(amount: Amount, quantity: number): Amount {
  if ('displayText' in amount) {
    // If multiplying by 0, return numeric 0 instead of preserving displayText
    if (quantity === 0) {
      return { min: 0, max: 0 };
    }
    return amount;
  }

  const value = parseNumericAmount(amount);
  const effectiveQuantity = !isNaN(quantity) && quantity > 0 ? quantity : 0;

  const resultMin = value.min * effectiveQuantity;
  let resultMax: number | null;

  if (value.max === null) {
    resultMax = null;
  } else {
    resultMax = value.max * effectiveQuantity;
  }

  return {
    min: resultMin,
    max: resultMax,
  };
}

/**
 * Formats a number according to locale settings.
 */
export function formatNumber(num: number, opts?: Intl.NumberFormatOptions): string {
  const hasDecimals = num % 1 !== 0;
  const defaultOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  };

  return new Intl.NumberFormat('en-US', { ...defaultOptions, ...opts }).format(num);
}

/**
 * Creates a currency formatting function for a specific currency type.
 */
export const getCurrencyFormatter =
  (currencyType: CurrencyType) =>
  (currencyAmount: number): string => {
    // Handle floating-point precision issues by rounding to cents first
    const roundedAmount = Math.round(currencyAmount * 100) / 100;
    const hasDecimals = roundedAmount % 1 !== 0;

    return formatNumber(roundedAmount, {
      style: 'currency',
      currency: currencyType,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    });
  };

/**
 * Formats an Amount object into a display string using a specific currency.
 * - Returns displayText property if present.
 * - Formats numeric { min, max: number | null } as:
 * - "$XXX+" if max is null.
 * - "$XXX" if min and max are equal numbers.
 * - "$XXX - $YYY" if min and max are different numbers.
 * - Defaults to "Contact" for invalid/missing input or unhandled cases.
 */
export const getDisplayAmountForCurrency =
  (currencyType?: CurrencyType | null) =>
  (amount?: Amount | null): string => {
    const effectiveCurrencyType = currencyType ?? 'USD';
    const formatCurrency = getCurrencyFormatter(effectiveCurrencyType as CurrencyType);

    if (!amount) {
      return 'Contact';
    }

    if ('displayText' in amount && typeof amount.displayText === 'string') {
      return amount.displayText;
    }

    if ('min' in amount) {
      const min = amount.min ?? 0;
      const max = amount.max;

      if (isNaN(min)) {
        return 'Contact';
      }

      if (max === null) {
        return `${formatCurrency(min)}+`;
      }

      if (typeof max === 'number' && !isNaN(max)) {
        const displayMax = Math.max(min, max);
        if (min === displayMax) {
          return formatCurrency(min);
        } else {
          return `${formatCurrency(min)} - ${formatCurrency(displayMax)}`;
        }
      }

      if (max === undefined || isNaN(max as number)) {
        return formatCurrency(min);
      }
    }

    return 'Contact';
  };
